/**
 * ============================================================================
 * TRANSACTION STATUS UTILITIES - Frontend
 * ============================================================================
 * 
 * Frontend utilities for understanding transaction status transitions,
 * valid actions, and status display.
 */

/**
 * Transaction status definitions
 */
export const TRANSACTION_STATUSES = {
  CREATED: 'created',
  LINK_SENT: 'link_sent',
  PAYMENT_PENDING: 'payment_pending',
  ESCROW_FUNDED: 'escrow_funded',
  IN_PROGRESS: 'in_progress',
  DELIVERED: 'delivered',
  DISPUTED: 'disputed',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  REFUNDED: 'refunded',
};

/**
 * Valid status transitions
 * Maps each status to the statuses it can transition to
 */
export const VALID_TRANSITIONS = {
  [TRANSACTION_STATUSES.CREATED]: [
    TRANSACTION_STATUSES.LINK_SENT,
    TRANSACTION_STATUSES.CANCELLED,
  ],
  [TRANSACTION_STATUSES.LINK_SENT]: [
    TRANSACTION_STATUSES.PAYMENT_PENDING,
    TRANSACTION_STATUSES.CANCELLED,
  ],
  [TRANSACTION_STATUSES.PAYMENT_PENDING]: [
    TRANSACTION_STATUSES.ESCROW_FUNDED,
    TRANSACTION_STATUSES.CANCELLED,
  ],
  [TRANSACTION_STATUSES.ESCROW_FUNDED]: [
    TRANSACTION_STATUSES.IN_PROGRESS,
    TRANSACTION_STATUSES.DISPUTED,
    TRANSACTION_STATUSES.CANCELLED,
  ],
  [TRANSACTION_STATUSES.IN_PROGRESS]: [
    TRANSACTION_STATUSES.DELIVERED,
    TRANSACTION_STATUSES.DISPUTED,
    TRANSACTION_STATUSES.CANCELLED,
  ],
  [TRANSACTION_STATUSES.DELIVERED]: [
    TRANSACTION_STATUSES.COMPLETED,
    TRANSACTION_STATUSES.DISPUTED,
  ],
  [TRANSACTION_STATUSES.DISPUTED]: [
    TRANSACTION_STATUSES.COMPLETED,
    TRANSACTION_STATUSES.IN_PROGRESS,
    TRANSACTION_STATUSES.CANCELLED,
    TRANSACTION_STATUSES.REFUNDED,
  ],
  [TRANSACTION_STATUSES.COMPLETED]: [], // Terminal state
  [TRANSACTION_STATUSES.CANCELLED]: [], // Terminal state
  [TRANSACTION_STATUSES.REFUNDED]: [], // Terminal state
};

/**
 * Status display labels
 */
export const STATUS_LABELS = {
  [TRANSACTION_STATUSES.CREATED]: 'Created',
  [TRANSACTION_STATUSES.LINK_SENT]: 'Link Sent',
  [TRANSACTION_STATUSES.PAYMENT_PENDING]: 'Payment Pending',
  [TRANSACTION_STATUSES.ESCROW_FUNDED]: 'Funded',
  [TRANSACTION_STATUSES.IN_PROGRESS]: 'In Progress',
  [TRANSACTION_STATUSES.DELIVERED]: 'Delivered',
  [TRANSACTION_STATUSES.DISPUTED]: 'Disputed',
  [TRANSACTION_STATUSES.COMPLETED]: 'Completed',
  [TRANSACTION_STATUSES.CANCELLED]: 'Cancelled',
  [TRANSACTION_STATUSES.REFUNDED]: 'Refunded',
};

/**
 * Status descriptions
 */
export const STATUS_DESCRIPTIONS = {
  [TRANSACTION_STATUSES.CREATED]: 'Transaction created and awaiting link sharing',
  [TRANSACTION_STATUSES.LINK_SENT]: 'Transaction link shared with counterparty',
  [TRANSACTION_STATUSES.PAYMENT_PENDING]: 'Counterparty viewing transaction, payment initiated',
  [TRANSACTION_STATUSES.ESCROW_FUNDED]: 'Payment received and funds securely held in escrow',
  [TRANSACTION_STATUSES.IN_PROGRESS]: 'Seller working on delivering the goods/services',
  [TRANSACTION_STATUSES.DELIVERED]: 'Seller has delivered goods/services for review',
  [TRANSACTION_STATUSES.DISPUTED]: 'Dispute raised - funds frozen until resolution',
  [TRANSACTION_STATUSES.COMPLETED]: 'Transaction completed successfully',
  [TRANSACTION_STATUSES.CANCELLED]: 'Transaction cancelled by one of the parties',
  [TRANSACTION_STATUSES.REFUNDED]: 'Transaction refunded to buyer',
};

/**
 * Status colors for UI
 */
export const STATUS_COLORS = {
  [TRANSACTION_STATUSES.CREATED]: 'gray',
  [TRANSACTION_STATUSES.LINK_SENT]: 'blue',
  [TRANSACTION_STATUSES.PAYMENT_PENDING]: 'yellow',
  [TRANSACTION_STATUSES.ESCROW_FUNDED]: 'green',
  [TRANSACTION_STATUSES.IN_PROGRESS]: 'purple',
  [TRANSACTION_STATUSES.DELIVERED]: 'indigo',
  [TRANSACTION_STATUSES.DISPUTED]: 'red',
  [TRANSACTION_STATUSES.COMPLETED]: 'green',
  [TRANSACTION_STATUSES.CANCELLED]: 'gray',
  [TRANSACTION_STATUSES.REFUNDED]: 'orange',
};

/**
 * Check if a status transition is valid
 * @param {string} currentStatus - Current transaction status
 * @param {string} newStatus - Desired new status
 * @returns {boolean} True if transition is valid
 */
export const isValidTransition = (currentStatus, newStatus) => {
  const allowedTransitions = VALID_TRANSITIONS[currentStatus] || [];
  return allowedTransitions.includes(newStatus);
};

/**
 * Get all valid next statuses for a transaction
 * @param {string} currentStatus - Current transaction status
 * @returns {Array<string>} Array of valid next statuses
 */
export const getValidNextStatuses = (currentStatus) => {
  return VALID_TRANSITIONS[currentStatus] || [];
};

/**
 * Check if status is terminal (no further transitions)
 * @param {string} status - Transaction status
 * @returns {boolean} True if status is terminal
 */
export const isTerminalStatus = (status) => {
  const transitions = VALID_TRANSITIONS[status] || [];
  return transitions.length === 0;
};

/**
 * Check if status allows cancellation
 * @param {string} status - Transaction status
 * @returns {boolean} True if status allows cancellation
 */
export const canCancel = (status) => {
  return getValidNextStatuses(status).includes(TRANSACTION_STATUSES.CANCELLED);
};

/**
 * Check if status allows dispute
 * @param {string} status - Transaction status
 * @returns {boolean} True if status allows dispute
 */
export const canDispute = (status) => {
  return getValidNextStatuses(status).includes(TRANSACTION_STATUSES.DISPUTED);
};

/**
 * Get status label
 * @param {string} status - Transaction status
 * @returns {string} Status label
 */
export const getStatusLabel = (status) => {
  return STATUS_LABELS[status] || status;
};

/**
 * Get status description
 * @param {string} status - Transaction status
 * @returns {string} Status description
 */
export const getStatusDescription = (status) => {
  return STATUS_DESCRIPTIONS[status] || '';
};

/**
 * Get status color
 * @param {string} status - Transaction status
 * @returns {string} Status color
 */
export const getStatusColor = (status) => {
  return STATUS_COLORS[status] || 'gray';
};

/**
 * Get status progress percentage (0-100)
 * @param {string} status - Transaction status
 * @returns {number} Progress percentage
 */
export const getStatusProgress = (status) => {
  const progressMap = {
    [TRANSACTION_STATUSES.CREATED]: 10,
    [TRANSACTION_STATUSES.LINK_SENT]: 20,
    [TRANSACTION_STATUSES.PAYMENT_PENDING]: 30,
    [TRANSACTION_STATUSES.ESCROW_FUNDED]: 40,
    [TRANSACTION_STATUSES.IN_PROGRESS]: 50,
    [TRANSACTION_STATUSES.DELIVERED]: 80,
    [TRANSACTION_STATUSES.DISPUTED]: 60,
    [TRANSACTION_STATUSES.COMPLETED]: 100,
    [TRANSACTION_STATUSES.CANCELLED]: 0,
    [TRANSACTION_STATUSES.REFUNDED]: 0,
  };

  return progressMap[status] || 0;
};

/**
 * Check if user can perform action based on role
 * @param {string} status - Current transaction status
 * @param {string} action - Action to perform
 * @param {string} userRole - User role (buyer, seller, creator, counterparty)
 * @returns {boolean} True if user can perform action
 */
export const canPerformAction = (status, action, userRole) => {
  switch (action) {
    case 'cancel':
      return canCancel(status) && (userRole === 'creator' || userRole === 'counterparty');
    
    case 'dispute':
      return canDispute(status) && (userRole === 'buyer' || userRole === 'seller');
    
    case 'deliver':
      return status === TRANSACTION_STATUSES.IN_PROGRESS && userRole === 'seller';
    
    case 'approve':
      return status === TRANSACTION_STATUSES.DELIVERED && userRole === 'buyer';
    
    case 'request_revision':
      return status === TRANSACTION_STATUSES.DELIVERED && userRole === 'buyer';
    
    default:
      return false;
  }
};

export default {
  TRANSACTION_STATUSES,
  VALID_TRANSITIONS,
  STATUS_LABELS,
  STATUS_DESCRIPTIONS,
  STATUS_COLORS,
  isValidTransition,
  getValidNextStatuses,
  isTerminalStatus,
  canCancel,
  canDispute,
  getStatusLabel,
  getStatusDescription,
  getStatusColor,
  getStatusProgress,
  canPerformAction,
};
